import { useEffect, useRef, useState } from 'preact/hooks';
import type { ComponentChildren } from 'preact';
import { HeadSound } from './HeadButtons';
import type { PublicPlayer } from '@snw/protocol';
import { muted, principal, serverNow, setMuted } from '../state/store';
import { sfx, unlockAudio } from '../audio/sfx';
export { Logo } from './Logo';

export type Expression = 'neutral' | 'thinking' | 'happy' | 'noway' | 'victory' | 'flirt';
export interface AvatarInfo { id: string; label: string; palette: string; states: Record<string, string>; /** visual presentation of the ARTWORK, for the 04-profile filter — a property of the drawing, not of any player */ presents?: 'female' | 'male' | 'other' }
let catalog: Map<string, AvatarInfo> = new Map();
export function setCatalog(list: AvatarInfo[]): void { catalog = new Map(list.map((a) => [a.id, a])); }
export function catalogList(): AvatarInfo[] { return [...catalog.values()]; }
/** the portrait file of one avatar in one expression (the player card draws the portraits itself, in Codex's windows) */
export function avatarSrc(avatarId: string, state: Expression): string | undefined { const a = catalog.get(avatarId); return a?.states[state] ?? a?.states['neutral']; }

/** Still-image expression avatar: swaps the sheet cell for the state, crossfades, punches on change.
 *  Without a finished asset it renders the deliberately obvious placeholder (solid electric-blue disc + initial) in the
 *  exact final geometry — see MISSING_MEDIA_ASSETS.md. No stand-in artwork. */
export function Avatar({ avatarId, name = '', state = 'neutral', size, ring, bob, fx, style, bare, lazy, cls: extra }: { avatarId: string; name?: string; state?: Expression; size?: number | string; ring?: 'target' | 'off'; bob?: boolean; fx?: 'pulse' | 'shake'; style?: Record<string, string | number>; /** an extra class for a context's own crop (the board's row disc) */ cls?: string; /** the portrait sits in a hole in drawn art that already has the ring: drop the CSS border, fill and shadow, and fill the hole exactly */ bare?: boolean; /** a long picker list: only what is near the viewport is fetched (the profile grid holds the whole 150-strong cast) */ lazy?: boolean }) {
  const a = catalog.get(avatarId);
  const src = a?.states[state] ?? a?.states['neutral'];
  const [prev, setPrev] = useState(src); const [kick, setKick] = useState(0);
  useEffect(() => { if (src !== prev) { setPrev(src); setKick((k) => k + 1); } }, [src]);
  /** The punch belongs to taking a state ON, not to letting it go. Selecting an avatar turns it happy and it pops;
      the one you just released went back to neutral and popped exactly the same way, which read as a second selection
      (owner 20.9.2026). Going back to neutral now settles instead: the same spring, inverted — a small dip rather
      than a bounce. */
  const motion = kick ? (state === 'neutral' ? 'settle' : 'pulse') : '';
  const cls = ['avatar', bare ? 'bare' : '', `state-${state}`, ring ? `ring-${ring}` : '', bob ? 'bob' : '', fx ?? '', motion, extra ?? ''].filter(Boolean).join(' ');
  const sz = size === undefined ? undefined : typeof size === 'number' ? { width: size, height: size } : { width: size, height: size };
  return (
    <div class={cls} key={kick} style={{ ...(sz ?? {}), ...(style ?? {}) }} data-avatar={avatarId} data-state={state}>
      {src ? <img src={src} alt="" draggable={false} loading={lazy ? 'lazy' : 'eager'} decoding={lazy ? 'async' : undefined} /> : <span class="initial" data-placeholder="avatar">{name.trim() ? name.trim()[0]!.toUpperCase() : (avatarId.match(/(\d+)$/)?.[1] ?? '·').replace(/^0+/, '')}</span>}
    </div>
  );
}

export function Roster({ players, expr, statusOf, compact, slots }: { players: PublicPlayer[]; expr: (p: PublicPlayer) => Expression; statusOf?: (p: PublicPlayer) => { text: string; cls?: string } | null; compact?: boolean; slots?: number }) {
  const empty = slots ? Math.max(0, Math.min(6, slots) - players.length) : 0;
  const n = Math.min(6, Math.max(2, players.length + empty)); const meId = principal.value?.id;
  return (
    <div class={`roster n${n} ${compact ? 'compact' : ''}`} data-testid="roster">
      {players.slice(0, 6).map((p) => { const s = statusOf?.(p); return (
        <div class={`seat ${p.id === meId ? 'me' : ''}`} key={p.id} data-player={p.displayName}>
          <span class="num" aria-hidden="true">{p.seat + 1}</span>
          <Avatar avatarId={p.avatarId} name={p.displayName} state={expr(p)} ring={!p.connected ? 'off' : undefined} bob={p.connected && !compact} />
          <div class="name">{p.displayName}</div>
          {s && !compact && <div class={`status ${s.cls ?? ''}`}>{s.text}</div>}
        </div>); })}
      {Array.from({ length: Math.max(0, Math.min(empty, 6 - Math.min(6, players.length))) }, (_, i) => (
        <div class="seat empty" key={`empty-${i}`} aria-hidden="true"><span class="num">{players.length + i + 1}</span><div class="avatar"><span class="initial plus">+</span></div><div class="name">Invite</div>{!compact && <div class="status">Open</div>}</div>))}
    </div>
  );
}

export function Timer({ deadlineAt }: { deadlineAt: number | null }) {
  const [left, setLeft] = useState(0); const lastTick = useRef(-1);
  useEffect(() => {
    const id = setInterval(() => { const s = deadlineAt ? Math.max(0, Math.ceil((deadlineAt - serverNow()) / 1000)) : 0; setLeft(s); if (s <= 5 && s > 0 && s !== lastTick.current) { lastTick.current = s; sfx.tick(); } }, 200);
    return () => clearInterval(id);
  }, [deadlineAt]);
  if (!deadlineAt) return null;
  const mm = String(Math.floor(left / 60)).padStart(2, '0'), ss = String(left % 60).padStart(2, '0');
  return <div class={`timer ${left <= 5 ? 'urgent' : ''}`} data-testid="timer">⏳ {mm}:{ss}</div>;
}

export function MuteButton() {
  // The one sound button of the game (OD-033): the Profile edit screen's own two-layer disc, mounted once by the shell,
  // top-right. It sits UNDER the sheet scrim by the `.mute` rule in the stylesheet, not by an inline z.
  return <HeadSound />;
}

/** App-shell screen: TOP / MAIN STAGE / ACTION DOCK, never taller than the viewport. */
export function Screen({ top, children, dock, decor, class: cls = '', stageClass = '', testid }: { top?: ComponentChildren; children: ComponentChildren; dock?: ComponentChildren; decor?: ComponentChildren; class?: string; stageClass?: string; testid?: string }) {
  return (
    <div class={`screen enter ${cls}`} data-testid={testid}>
      {decor && <div class="decor" aria-hidden="true">{decor}</div>}
      <div class="top">{top}</div>
      <div class={`stage ${stageClass}`}>{children}</div>
      <div class="dock" data-testid="dock">{dock}</div>
    </div>
  );
}

/** Neon graffiti sticker (marker font) — decoration only, absolutely positioned, never part of layout. */
export function Graffiti({ text, color, style, rot = -8 }: { text: string; color: 'pink' | 'cyan' | 'gold' | 'purple'; style: Record<string, string | number>; rot?: number }) {
  return <div class={`graffiti ${color}`} aria-hidden="true" style={{ transform: `rotate(${rot}deg)`, ...style }}>{text}</div>;
}
